import React from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';

export default function LoginPage() {
  const [isSignUpMode, setIsSignUpMode] = React.useState(false);
  const [signInData, setSignInData] = React.useState({ email: '', password: '' });
  const [signUpData, setSignUpData] = React.useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    referralCode: '',
  });
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [googleBusy, setGoogleBusy] = React.useState(false);
  const { isAuthenticated, login, loginWithGoogleCode, register, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath = location.state?.redirectTo || '/';
  const redirectPathRef = React.useRef(redirectPath);

  React.useEffect(() => {
    redirectPathRef.current = redirectPath;
  }, [redirectPath]);

  if (isAuthenticated) return <Navigate to={redirectPath} replace />;

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).toLowerCase());

  const handleSignInSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!validateEmail(signInData.email)) return setError('Kérjük, adj meg egy érvényes email címet.');
    const result = await login(signInData.email.trim().toLowerCase(), signInData.password);
    if (!result.success) return setError(result.error || 'Érvénytelen email vagy jelszó.');
    navigate(redirectPath, { replace: true });
  };

  const handleSignUpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!validateEmail(signUpData.email)) return setError('Kérjük, adj meg egy érvényes email címet.');
    if (signUpData.password !== signUpData.confirmPassword) return setError('A jelszavak nem egyeznek.');
    if (signUpData.password.trim().length < 6) return setError('A jelszó legalább 6 karakter legyen.');
    const result = await register(
      signUpData.username.trim(),
      signUpData.email.trim().toLowerCase(),
      signUpData.password.trim(),
      signUpData.referralCode?.trim().toUpperCase() || '',
    );
    if (!result.success) return setError(result.error || 'A regisztráció sikertelen.');
    setSuccess('Sikeres regisztráció! Átirányítás…');
    navigate(redirectPath, { replace: true });
  };

  const loadGoogleScript = () =>
    new Promise((resolve, reject) => {
      if (window.google?.accounts?.id) return resolve();
      const existing = document.getElementById('google-identity-script');
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Google script load failed.')), { once: true });
        return;
      }
      const script = document.createElement('script');
      script.id = 'google-identity-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Google script load failed.'));
      document.head.appendChild(script);
    });

  const handleGoogleSignIn = async () => {
    setError('');
    setSuccess('');
    setGoogleBusy(true);
    try {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      if (!clientId) throw new Error('Hiányzik: REACT_APP_GOOGLE_CLIENT_ID a frontend .env-ben.');
      await loadGoogleScript();
      if (!window.google?.accounts?.oauth2) throw new Error('Google OAuth API nem elérhető.');
      const codeClient = window.google.accounts.oauth2.initCodeClient({
        client_id: clientId,
        scope: 'openid email profile',
        ux_mode: 'popup',
        callback: async (response) => {
          if (!response?.code) {
            setGoogleBusy(false);
            setError('Google bejelentkezés megszakítva.');
            return;
          }
          const result = await loginWithGoogleCode(response.code);
          setGoogleBusy(false);
          if (!result.success) {
            setError(result.error || 'Google bejelentkezés sikertelen.');
            return;
          }
          navigate(redirectPathRef.current, { replace: true });
        },
      });
      codeClient.requestCode();
      window.setTimeout(() => {
        setGoogleBusy((busy) => {
          if (busy) {
            setError(
              'A Google bejelentkezés most nem elérhető. Ellenőrizd az OAuth origin beállítást és próbáld újra.',
            );
            return false;
          }
          return busy;
        });
      }, 5000);
    } catch (e) {
      setGoogleBusy(false);
      setError(e.message || 'Google bejelentkezés nem indítható.');
    }
  };

  return (
    <main className="auth-page">
      <div className="auth-shell">
        <div className="auth-card auth-card--split">
          <section className="auth-main">
            <h1>{isSignUpMode ? 'Regisztráció' : 'Bejelentkezés'}</h1>
            {!isSignUpMode && (
              <p className="auth-subtitle">Lépjen be fiókjába az ajánlatok és megrendelések kezeléséhez.</p>
            )}
            {error && <div className="admin-error">{error}</div>}
            {success && <div className="admin-note">{success}</div>}

            {!isSignUpMode ? (
              <form onSubmit={handleSignInSubmit} className="auth-form">
                <div className="auth-input-wrap">
                  <span className="auth-input-icon" aria-hidden="true">
                    @
                  </span>
                  <input
                    type="email"
                    placeholder="E-mail cím"
                    value={signInData.email}
                    onChange={(e) => setSignInData((s) => ({ ...s, email: e.target.value }))}
                    required
                  />
                </div>
                <div className="auth-input-wrap">
                  <span className="auth-input-icon" aria-hidden="true">
                    •
                  </span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Jelszó"
                    value={signInData.password}
                    onChange={(e) => setSignInData((s) => ({ ...s, password: e.target.value }))}
                    required
                  />
                  <button
                    type="button"
                    className="auth-eye-btn"
                    aria-label={showPassword ? 'Jelszó elrejtése' : 'Jelszó megjelenítése'}
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? '🙈' : '👁'}
                  </button>
                </div>
                <button type="submit" className="primary-btn auth-submit-btn" disabled={loading}>
                  {loading ? 'Bejelentkezés…' : 'Bejelentkezés'}
                </button>
                <p className="auth-links auth-links--center">
                  <Link to="/forgot-password">Elfelejtette a jelszavát?</Link>
                </p>
                <div className="auth-divider">
                  <span>vagy</span>
                </div>
                <button
                  type="button"
                  className="secondary-btn auth-google-btn"
                  onClick={handleGoogleSignIn}
                  disabled={googleBusy}
                >
                  <span className="auth-google-g">G</span>{' '}
                  {googleBusy ? 'Google bejelentkezés…' : 'Belépés Google-fiókkal'}
                </button>
                <p className="auth-links auth-links--center">
                  Nincs még fiókja?{' '}
                  <button type="button" className="auth-link-btn" onClick={() => setIsSignUpMode(true)}>
                    Regisztráció
                  </button>
                </p>
              </form>
            ) : (
              <form onSubmit={handleSignUpSubmit} className="auth-form">
                <input
                  type="text"
                  placeholder="Felhasználónév"
                  value={signUpData.username}
                  onChange={(e) => setSignUpData((s) => ({ ...s, username: e.target.value }))}
                  required
                />
                <input
                  type="email"
                  placeholder="E-mail cím"
                  value={signUpData.email}
                  onChange={(e) => setSignUpData((s) => ({ ...s, email: e.target.value }))}
                  required
                />
                <input
                  type="password"
                  placeholder="Jelszó"
                  value={signUpData.password}
                  onChange={(e) => setSignUpData((s) => ({ ...s, password: e.target.value }))}
                  required
                />
                <input
                  type="password"
                  placeholder="Jelszó megerősítése"
                  value={signUpData.confirmPassword}
                  onChange={(e) => setSignUpData((s) => ({ ...s, confirmPassword: e.target.value }))}
                  required
                />
                <input
                  type="text"
                  placeholder="Ajánlói kód (opcionális)"
                  value={signUpData.referralCode}
                  onChange={(e) => setSignUpData((s) => ({ ...s, referralCode: e.target.value }))}
                />
                <button type="submit" className="primary-btn auth-submit-btn" disabled={loading}>
                  {loading ? 'Regisztráció…' : 'Regisztráció'}
                </button>
                <p className="auth-links auth-links--center">
                  Van már fiókja?{' '}
                  <button type="button" className="auth-link-btn" onClick={() => setIsSignUpMode(false)}>
                    Bejelentkezés
                  </button>
                </p>
              </form>
            )}
          </section>

          <aside className="auth-side">
            <div className="auth-side-hero" />
            <div className="auth-side-points">
              <div className="auth-side-point">
                <span className="auth-side-point-icon">🛡</span>
                <div>
                <strong>Biztonságos és megbízható</strong>
                <p>Adatai védelme kiemelt fontosságú számunkra.</p>
                </div>
              </div>
              <div className="auth-side-point">
                <span className="auth-side-point-icon">🕒</span>
                <div>
                <strong>Gyors és egyszerű</strong>
                <p>Néhány kattintással elérheti ajánlatait és megrendeléseit.</p>
                </div>
              </div>
              <div className="auth-side-point">
                <span className="auth-side-point-icon">🎧</span>
                <div>
                <strong>Ügyféltámogatás</strong>
                <p>Kérdése van? Csapatunk készséggel áll rendelkezésére.</p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
